#!/usr/bin/env node
import { Command } from 'commander';
import chalk from 'chalk';
import { readFileSync, existsSync } from 'fs';
import { join, dirname } from 'path';
import { fileURLToPath } from 'url';
import { createAuthCommand } from '../commands/auth.js';
import { createLoginCommand } from '../commands/login.js';
import { createPromptsCommand } from '../commands/prompts/index.js';
import { createTracesCommand } from '../commands/traces/index.js';
import { createIntegrateCommand } from '../commands/integrate.js';
import { createAgentsCommand } from '../commands/agents/index.js';
import { createConfigCommand } from '../commands/config.js';
import { createPlaygroundCommand } from '../commands/playground.js';
import { createWorkspacesCommand } from '../commands/workspaces.js';
import { createProvidersCommand } from '../commands/providers/index.js';
import { createInitCommand } from '../commands/init.js';
import { createExploreCommand } from '../commands/explore.js';
import { createSkillsCommand } from '../commands/skills.js';
import { createUsageCommand } from '../commands/usage.js';
import { createHooksCommand } from '../commands/hooks/index.js';
import { createFeedbackCommand } from '../commands/feedback.js';
import { hasCredentials } from '../lib/config.js';
import { hasRcConfig } from '../commands/init.js';
import { AGENTS_WIP_BANNER } from '../lib/agents-wip-banner.js';
import { setCliVersion, getCompatMetadata } from '../lib/output.js';

/**
 * CLI Context Interface
 * Passed to all subcommands for shared state
 */
export interface CliContext {
  /** Output results as JSON */
  json: boolean;
  /** MutagenT API key */
  apiKey?: string;
  /** MutagenT server endpoint */
  endpoint?: string;
  /** Enable verbose logging */
  verbose: boolean;
}

// Read version: compile-time define > package.json > hardcoded fallback
let cliVersion = '0.1.1';
if (process.env.CLI_VERSION) {
  cliVersion = process.env.CLI_VERSION;
} else {
  try {
    const __dirname = dirname(fileURLToPath(import.meta.url));
    const pkgPath = join(__dirname, '..', '..', 'package.json');
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8')) as { version?: string };
    cliVersion = pkg.version ?? cliVersion;
  } catch {
    // Fallback to hardcoded version
  }
}

// Register cliVersion for _compat metadata injection (Scope D)
setCliVersion(cliVersion);

const program = new Command();

program
  .name('mutagent')
  .description(`MutagenT CLI - AI-native prompt optimization platform

  Documentation: https://docs.mutagent.io/cli
  Dashboard:     https://app.mutagent.io`)
  .option('-v, --version', 'Display the version number')
  .option('--json', 'Output results as JSON (for AI agents)')
  .option('--api-key <key>', 'MutagenT API key')
  .option('--endpoint <url>', 'MutagenT server endpoint')
  .option('--non-interactive', 'Disable interactive prompts (for CI/AI agents)')
  .configureHelp({
    sortSubcommands: true,
    showGlobalOptions: true,
  });

// Central help hub — self-contained navigation for CI/CD, coding agents, and humans
program.addHelpText('after', `
${chalk.bold.cyan('WORKFLOWS:')}
  ${chalk.bold('Setup')}                  mutagent login  →  mutagent init  →  mutagent skills install
  ${chalk.bold('Framework Integration')}  mutagent explore  →  mutagent integrate <fw>  →  mutagent traces
  ${chalk.bold('Prompt Optimization')}    mutagent prompts create  →  prompts dataset  →  prompts evaluation  →  prompts optimize
  ${chalk.bold('Agent Optimization')}     ${chalk.dim('[coming soon — see: mutagent agents --help]')}

${chalk.dim('For rubric design, prompt-vs-agent classification, scorecard interpretation,')}
${chalk.dim('see the Skill at')} ${chalk.cyan('.claude/skills/mutagent-cli/SKILL.md')} ${chalk.dim('(install via')} ${chalk.cyan('mutagent skills install')}${chalk.dim(')')}

${AGENTS_WIP_BANNER}

${chalk.yellow('Non-Interactive Mode (CI/CD & Coding Agents):')}
  export MUTAGENT_API_KEY=mt_...  ${chalk.dim('or')}  --api-key mt_...
  --json ${chalk.dim('for structured output')}    --non-interactive ${chalk.dim('to disable prompts')}

${chalk.yellow('Command Navigation:')}
  mutagent login                        ${chalk.dim('Login (browser OAuth — recommended)')}
  mutagent auth status                  ${chalk.dim('Check auth + workspace')}
  mutagent init                         ${chalk.dim('Initialize project (.mutagentrc.json)')}
  mutagent explore                      ${chalk.dim('Discover prompts in codebase')}
  mutagent workspaces list --json       ${chalk.dim('List workspaces (verify ID)')}
  mutagent config set workspace <id>    ${chalk.dim('Set active workspace')}
  mutagent usage --json                 ${chalk.dim('Show account usage + provider status')}

  mutagent prompts create --help        ${chalk.dim('Upload prompt (read help first!)')}
  mutagent prompts list --json          ${chalk.dim('List prompts')}
  mutagent prompts get <id> --json      ${chalk.dim('Full prompt details + schemas')}

  mutagent prompts dataset add --help   ${chalk.dim('Upload dataset (read help first!)')}
  mutagent prompts dataset list <id>    ${chalk.dim('List datasets')}

  mutagent prompts evaluation create --help           ${chalk.dim('Create eval (read help first!)')}
  mutagent prompts evaluation create <id> --guided --json   ${chalk.dim('Guided eval workflow')}
  mutagent prompts evaluation list <id> --json        ${chalk.dim('List evaluations')}

  mutagent providers list --json              ${chalk.dim('List configured BYOK providers')}
  mutagent providers list --models            ${chalk.dim('see available models per provider')}

  mutagent prompts optimize start --help      ${chalk.dim('Run optimization (read help first!)')}
  mutagent prompts optimize status <job-id>   ${chalk.dim('Poll progress')}
  mutagent prompts optimize results <job-id>  ${chalk.dim('View scorecard')}

  mutagent feedback send -m "..."         ${chalk.dim('Send product feedback')}
  mutagent feedback send -m "..." --category bug  ${chalk.dim('Report a bug')}

  mutagent integrate <framework>        ${chalk.dim('Framework integration guide')}

${chalk.bold.red('Report Issues:')}
  Hit a bug? Run: ${chalk.cyan('mutagent feedback send --category bug -m "describe what went wrong"')}
  With session context (for AI agents): ${chalk.cyan('mutagent feedback send --category bug --context - -m "..."')}
  (pipe JSON session summary via stdin for richer reports)
  mutagent hooks --help                 ${chalk.dim('Hook setup for Claude Code telemetry')}
  mutagent skills install               ${chalk.dim('Install the MutagenT CLI skill for AI agents')}
  mutagent playground run <id> --input '{...}'  ${chalk.dim('Quick test')}

${chalk.yellow('★ Workflow: Framework Integration (Tracing):')}
  1. mutagent explore                                    ${chalk.dim('← discover prompts/agents in codebase')}
  2. mutagent integrate <framework>                      ${chalk.dim('← get integration instructions')}
  3. Apply tracing code to your codebase                 ${chalk.dim('← follow the guide output')}
  4. mutagent traces list --json                         ${chalk.dim('← verify traces are arriving')}

${chalk.yellow('★ Workflow: Evaluate → Optimize:')}
  1. mutagent prompts create --help               ${chalk.dim('← read help')}
  2. mutagent prompts create ... --json            ${chalk.dim('← upload prompt with {variables} + inputSchema')}
  3. mutagent prompts dataset add --help           ${chalk.dim('← read help')}
  4. mutagent prompts dataset add <id> ... --json  ${chalk.dim('← upload dataset')}
  5. mutagent prompts evaluation create <id> --guided --json  ${chalk.dim('← guided eval')}
  6. mutagent prompts optimize start <id> --dataset <d> --evaluation <e> --json

${chalk.yellow('Post-Onboarding Decision Tree:')}
  After ${chalk.bold('mutagent auth login')}, users land in one of 3 paths:
  ${chalk.bold('Path A')} (Tracing):      explore → integrate <framework> → apply tracing → verify
  ${chalk.bold('Path B')} (Optimization): explore → prompts create → dataset add → eval create → optimize
  ${chalk.bold('Path C')} (Manual):       Use CLI commands directly — run mutagent <command> --help

${chalk.yellow('Directive System:')}
  Every --json response may include:
  ${chalk.bold('_directive.display')}      Type tag — 'status_card' for card-kind directives (drives test/docs guards)
  ${chalk.bold('_directive.renderedCard')} Pre-formatted card ${chalk.red('(MUST echo verbatim in chat whenever this field exists — see SKILL.md Verbatim Card Display Protocol)')}
  ${chalk.bold('_directive.instruction')}  Next step for the agent (self-sufficient, no Skill required)
  ${chalk.bold('_directive.next')}         Array of suggested follow-up commands
  ${chalk.bold('_directive.decisionTree')} Multi-step branching logic for guided flows
  ${chalk.bold('_directive.askUserQuestions')} Questions to collect from the user (inside _directive)
  ${chalk.bold('_links')}                  Dashboard/API URLs (format as markdown links)
  ${chalk.bold('_compat')}                 Compat metadata: cliVersion, skillVersion, skillMinCliVersion

${chalk.yellow('Evaluation Criteria Format:')}
  Each criterion MUST have: ${chalk.bold('name')}, ${chalk.bold('description')} (scoring rubric), ${chalk.bold('evaluationParameter')}
  evaluationParameter MUST match an inputSchema or outputSchema field name
  No duplicate evaluationParameter values — each criterion targets a unique field
  ALL schema fields must be covered (missing fields = error)
  Use ${chalk.bold('--guided --json')} to generate criteria templates from prompt schemas

  ${chalk.bold('Two framings — coding agents MUST distinguish:')}
  ${chalk.bold('• INPUT fields → Minimum Viable Context')}
    What info MUST be present in the input for the prompt to succeed.
    Rubric should include a ${chalk.bold('completeness scale')} (e.g., 0 = missing critical context, 5 = complete).
    ${chalk.dim('Input fields are typically `{variables}` in your prompt template.')}
  ${chalk.bold('• OUTPUT fields → Output Standards')}
    Content correctness + structural correctness + a ${chalk.bold('correctness scale')} (e.g., 0 = wrong, 5 = perfect).

${chalk.yellow('Optimization Cost Control:')}
  Default max-iterations is 1. ${chalk.red('NEVER increase without explicit user request.')}
  Each iteration incurs LLM costs — confirm with user before starting >1.

${chalk.yellow('Post-Optimization:')}
  After ${chalk.bold('optimize results')}: ALWAYS show the before/after diff to the user first.
  Then offer choices: ${chalk.bold('Apply')} / ${chalk.bold('Reject')}.

${chalk.yellow('State Tracking:')}
  .mutagent/mutation-context.md — Codebase index of discovered/uploaded prompts
  Update after explore, create, and dataset operations
  mutagent auth status — Auth + workspace state
  Comment markers (// MutagenT:START ... // MutagenT:END) in source files

${chalk.yellow('AI Agent Rules (MANDATORY for coding agents):')}
  0. If .claude/skills/mutagent-cli/SKILL.md does not exist, run mutagent skills install first
  1. Login (two paths):
     - CI / fully automated: export MUTAGENT_API_KEY=mt_... then mutagent login --json
     - Helping a user onboard: mutagent login --browser --json
       (CLI prints an auth URL — surface it to the user verbatim, then the CLI
        polls for up to 5 minutes while the user authorizes in their browser.
        This IS agent-compatible. --non-interactive is NOT needed with --browser.)
  2. EVERY command MUST include --json (no exceptions)
  3. Run <command> --help BEFORE first use of any command
  4. Use --guided --json for evaluation creation (NEVER --guided alone)
  5. Parse _directive.renderedCard and copy it into your CHAT RESPONSE verbatim
     ${chalk.red('HARD STOP')}: do NOT run further commands until the card is rendered in chat
  6. After mutagent init, verify workspace: mutagent workspaces list --json
  7. Use {single_braces} for template variables in prompts
  8. Collect evaluation criteria from the user — NEVER auto-generate
  9. ALL user interaction via AskUserQuestion — CLI is non-interactive
${!hasCredentials() ? '\n' + chalk.yellow('  Warning: Not authenticated. Run: mutagent login') + '\n' : ''}${!hasRcConfig() ? '\n' + chalk.green('  Get started: mutagent init') + '\n' : ''}`);

// Handle --version flag early (before parse) since it's a global option, not a subcommand
// Commander's preAction hook only fires with subcommands — raw argv check is needed
const rawArgs = process.argv.slice(2);
if (rawArgs.includes('-v') || rawArgs.includes('--version')) {
  if (rawArgs.includes('--json')) {
    // Scope D: include _compat metadata so the Skill can validate compatibility
    console.log(JSON.stringify({
      version: cliVersion,
      _compat: getCompatMetadata(cliVersion),
    }));
  } else {
    console.log(cliVersion);
  }
  process.exit(0);
}

// Propagate global options to env vars before subcommands run
// This ensures --api-key, --endpoint, and --non-interactive flags are picked up
program.hook('preAction', (thisCommand) => {
  const globalOpts: { apiKey?: string; endpoint?: string; nonInteractive?: boolean } = thisCommand.optsWithGlobals();
  if (globalOpts.apiKey && !process.env.MUTAGENT_API_KEY) {
    process.env.MUTAGENT_API_KEY = globalOpts.apiKey;
  }
  if (globalOpts.endpoint && !process.env.MUTAGENT_ENDPOINT) {
    process.env.MUTAGENT_ENDPOINT = globalOpts.endpoint;
  }
  if (globalOpts.nonInteractive || process.env.CI === 'true' || !process.stdin.isTTY) {
    process.env.MUTAGENT_NON_INTERACTIVE = 'true';
  }
});

// Add commands
program.addCommand(createInitCommand());     // Init wizard
program.addCommand(createLoginCommand());    // Top-level login
program.addCommand(createAuthCommand());
program.addCommand(createConfigCommand());
program.addCommand(createPromptsCommand());
program.addCommand(createTracesCommand());
program.addCommand(createIntegrateCommand());
program.addCommand(createAgentsCommand());
program.addCommand(createPlaygroundCommand());
program.addCommand(createWorkspacesCommand());
program.addCommand(createProvidersCommand());
program.addCommand(createExploreCommand());   // Codebase scanner
program.addCommand(createSkillsCommand());    // Skills install
program.addCommand(createUsageCommand());     // Usage & quota
program.addCommand(createHooksCommand());     // Hook handlers (Claude Code telemetry)
program.addCommand(createFeedbackCommand());  // Product feedback

// C5: Runtime SKILL check — print a hint if SKILL is missing in interactive mode
// Only print when running interactively (not --json, not CI, not hooks/skills commands)
const isInteractive = process.stdin.isTTY && !rawArgs.includes('--json') && process.env.CI !== 'true';
const isSkillCommand = rawArgs[0] === 'skills' || rawArgs[0] === 'hooks';
if (isInteractive && !isSkillCommand) {
  const skillPath = join(process.cwd(), '.claude/skills/mutagent-cli/SKILL.md');
  if (!existsSync(skillPath)) {
    console.log(
      chalk.dim('MutagenT SKILL not installed. Install it for AI agent support? Run:'),
      chalk.cyan('mutagent skills install')
    );
  }
}

// Parse arguments
program.parse();
